/**
 * DataTable Component
 * Reusable table with consistent styling
 */

import { ReactNode } from 'react';
import { HIDE_BELOW_BREAKPOINTS, HIDE_CELL_BELOW, SHOW_CELL_BELOW, type HideBelowBreakpoint } from '@/constants/styles';

// Column definition
export interface Column<T> {
    key: string;
    header: string;
    align?: 'left' | 'center' | 'right';
    width?: string;
    /** Hide the column on viewports narrower than this breakpoint */
    hideBelow?: HideBelowBreakpoint;
    /** Extra classes for the column's header and body cells */
    className?: string;
    render?: (item: T, index: number) => ReactNode;
}

interface DataTableProps<T> {
    columns: Column<T>[];
    data: T[];
    keyExtractor: (item: T) => string | number;
    rowClassName?: (item: T, index: number) => string;
    onRowClick?: (item: T) => void;
    emptyMessage?: string;
    className?: string;
}

// Table header cell styling
const thBaseClass = 'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider';
const thAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

// Table body cell styling
const tdBaseClass = 'px-4 py-4';
const tdAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

const columnClass = <T,>(col: Column<T>) =>
    `${col.hideBelow ? HIDE_CELL_BELOW[col.hideBelow] : ''} ${col.className || ''}`.trim();

/**
 * DataTable - A reusable table component with consistent styling
 *
 * @example
 * const columns: Column<User>[] = [
 *   { key: 'name', header: 'Name', render: (user) => user.name },
 *   { key: 'email', header: 'Email', render: (user) => user.email },
 *   { key: 'status', header: 'Status', align: 'center', render: (user) => <StatusBadge status={user.status} /> },
 * ];
 *
 * <DataTable
 *   columns={columns}
 *   data={users}
 *   keyExtractor={(user) => user.id}
 * />
 */
export function DataTable<T>({
    columns,
    data,
    keyExtractor,
    rowClassName,
    onRowClick,
    emptyMessage = 'No data found',
    className = '',
}: DataTableProps<T>) {
    if (data.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p>{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className={`overflow-x-auto ${className}`}>
            <table className="w-full">
                <thead className="bg-gray-50">
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={`${thBaseClass} ${thAlignClass[col.align || 'left']} ${columnClass(col)}`}
                                style={col.width ? { width: col.width } : undefined}
                            >
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {data.map((item, index) => (
                        <tr
                            key={keyExtractor(item)}
                            className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(item, index) || ''}`}
                            onClick={onRowClick ? () => onRowClick(item) : undefined}
                        >
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    className={`${tdBaseClass} ${tdAlignClass[col.align || 'left']} ${columnClass(col)}`}
                                >
                                    {col.render
                                        ? col.render(item, index)
                                        : (item as Record<string, unknown>)[col.key] as ReactNode}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * TableFilters - A wrapper for filter controls above a table. Stacked on
 * a phone; from `sm` up the controls sit in a row that wraps instead of
 * pushing the last select and the refresh button off a tablet screen.
 */
interface TableFiltersProps {
    children: ReactNode;
    className?: string;
}

export function TableFilters({ children, className = '' }: TableFiltersProps) {
    return (
        <div className={`flex flex-col sm:flex-row sm:flex-wrap gap-4 mb-6 ${className}`}>
            {children}
        </div>
    );
}

/**
 * CollapsedColumns - the values of a row's `hideBelow` columns, shown
 * under the primary cell while their column is hidden. Each item is
 * visible exactly on the viewports where its column is not, so a phone,
 * a tablet and a half-width laptop window all keep the whole row
 * instead of losing what used to be reachable by a sideways scroll.
 */
export interface CollapsedColumn {
    key: string;
    /** The breakpoint the column itself is hidden below. */
    hideBelow: HideBelowBreakpoint;
    /** Printed in front of a value that is ambiguous on its own. */
    label?: string;
    /** `null`, `undefined`, `false` or '' leaves the item out. */
    value: ReactNode;
}

interface CollapsedColumnsProps {
    items: CollapsedColumn[];
    className?: string;
}

const hasValue = (item: CollapsedColumn) =>
    item.value !== null && item.value !== undefined && item.value !== false && item.value !== '';

/** The widest breakpoint among the items: above it nothing is collapsed. */
const widestBreakpoint = (items: CollapsedColumn[]): HideBelowBreakpoint =>
    items.reduce<HideBelowBreakpoint>(
        (widest, item) =>
            HIDE_BELOW_BREAKPOINTS.indexOf(item.hideBelow) > HIDE_BELOW_BREAKPOINTS.indexOf(widest) ? item.hideBelow : widest,
        'sm',
    );

export function CollapsedColumns({ items, className = '' }: CollapsedColumnsProps) {
    const shown = items.filter(hasValue);
    if (shown.length === 0) return null;

    return (
        <div
            data-testid="collapsed-columns"
            className={`mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 ${SHOW_CELL_BELOW[widestBreakpoint(shown)]} ${className}`.trim()}
        >
            {shown.map((item) => (
                <div key={item.key} className={`flex flex-wrap items-center gap-1 ${SHOW_CELL_BELOW[item.hideBelow]}`}>
                    {item.label && <span className="text-gray-400">{item.label}</span>}
                    {item.value}
                </div>
            ))}
        </div>
    );
}

export default DataTable;
